/**
 * Calculation Settings Component
 * Exactly matches simple ValuationCalculator UI structure
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Info, Percent, HelpCircle, Search } from 'lucide-react';

const CalculationSettings = ({
  // Method selection
  selectedMethod,
  setSelectedMethod,
  onShowMethodDetails,
  onShowHelp,

  // Industry selection props (two-tier system)
  industries,
  selectedParent,
  setSelectedParent,
  selectedIndustry,
  setSelectedIndustry,
  industrySearch,
  searchResults,
  showSearchDropdown,
  onIndustrySearchChange,
  onSelectIndustry,
  onDropdownClose,
  industryLoading,

  // Method-specific settings
  ccaMultipleType,
  setCcaMultipleType,
  manualMultipleType,
  setManualMultipleType,
  manualMultiple,
  setManualMultiple,
  privateDiscount,
  setPrivateDiscount,
  onShowDiscountInfo,
  methodInputs,
  setMethodInputs,
  capitalizationEarningsType,
  setCapitalizationEarningsType,
  businessType,
  setBusinessType,
  capitalizationRate,
  setCapitalizationRate,

  // Financial data
  financialData,
  currentYear,
  formatNumberWithCommas,

  // Industry data for benchmarks
  selectedIndustryDetails
}) => {
  // Extract unique industry parents for dropdown
  const industryParents = [...new Set(industries.map(ind => ind.industry_parent).filter(Boolean))].sort();

  // Filter industries by selected parent
  const filteredIndustries = selectedParent
    ? industries.filter(ind => ind.industry_parent === selectedParent)
    : [];

  // Handle parent dropdown change
  const handleParentChange = (e) => {
    const newParent = e.target.value;
    setSelectedParent(newParent);
    setSelectedIndustry(''); // Clear specific industry when parent changes
  };

  // Handle specific industry selection
  const handleIndustrySelect = (e) => {
    const industryId = e.target.value;
    setSelectedIndustry(industryId);
    // Don't update search bar when dropdown is used manually
    // Search bar should only update when searching and selecting from results
  };

  // Calculate current year financial summaries from multi-year data
  const currentYearRevenue = financialData?.revenue?.[currentYear] || 0;
  const currentYearEBITDA = financialData?.ebitda?.[currentYear] || 0;
  const currentYearSDE = financialData?.sde?.[currentYear] || 0;
  const currentYearNetIncome = financialData?.profit_after_tax?.[currentYear] || 0;
  const currentYearCashFlow = financialData?.free_cash_flow?.[currentYear] || 0;
  const currentYearAssets = financialData?.total_assets?.[currentYear] || 0;
  const currentYearLiabilities = financialData?.total_liabilities?.[currentYear] || 0;

  // Ref for search input to position dropdown
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Update dropdown position when it opens
  useEffect(() => {
    if (showSearchDropdown && searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [showSearchDropdown]);

  // Debug: Log when selectedParent or selectedIndustry changes
  useEffect(() => {
  }, [selectedParent, selectedIndustry]);

  return (
    <div>
      {/* Instructional Overview - At the top */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-lg p-6 mb-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-indigo-600 text-white rounded-full p-2 flex-shrink-0">
            <Info size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-indigo-900 mb-2">How to Use the Advanced Valuation Tool</h3>
            <p className="text-indigo-800 text-sm mb-4">
              This tool provides a comprehensive, multi-year valuation analysis using detailed financial data.
              Follow these steps to get an accurate valuation of your business:
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-indigo-200">
            <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
              <span className="bg-indigo-100 text-indigo-700 rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
              Configure Settings (This Tab)
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span><strong>Select your industry</strong> using search or dropdown menus to see relevant multiples</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span><strong>Choose a valuation method</strong> (CCA, DCF, Capitalization, NAV, or Manual)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span><strong>Adjust private company discount</strong> based on your business characteristics</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4 border border-indigo-200">
            <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
              <span className="bg-indigo-100 text-indigo-700 rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
              Enter Financial Data
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">📊</span>
                <span><strong>Income Tab:</strong> Revenue, expenses, EBITDA (last 4 years)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">🏦</span>
                <span><strong>Balance Tab:</strong> Assets, liabilities, equity (auto-calculates)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">💰</span>
                <span><strong>Cash Flow Tab:</strong> Operating cash flow data (required for DCF)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">➕</span>
                <span><strong>Add-backs Tab:</strong> One-time expenses to adjust EBITDA</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4 border border-indigo-200">
            <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
              <span className="bg-indigo-100 text-indigo-700 rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
              Review Analysis
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-purple-600 mt-0.5">🔢</span>
                <span><strong>Ratios Tab:</strong> View profitability, liquidity, and leverage metrics</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600 mt-0.5">📈</span>
                <span><strong>Charts Tab:</strong> Visualize financial trends over time</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4 border border-indigo-200">
            <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
              <span className="bg-indigo-100 text-indigo-700 rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
              Calculate Valuation
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-orange-600 mt-0.5">🧮</span>
                <span><strong>Click &quot;Calculate&quot;</strong> to generate your detailed valuation report</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 mt-0.5">💡</span>
                <span>Results will show estimated value, multiples applied, and assumptions used</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Business Information Header with Search */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Business Information</h2>

        {/* Industry Search Box */}
        <div className="relative w-64">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search industries..."
              value={industrySearch}
              onChange={(e) => onIndustrySearchChange(e.target.value)}
              onFocus={() => industrySearch.length >= 2 && showSearchDropdown}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Search Dropdown - Using portal-like fixed positioning to escape overflow container */}
          {showSearchDropdown && searchResults.length > 0 && (
            <div
              className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
              style={{
                width: '256px', // matches w-64
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`
              }}
            >
              {searchResults.map((industry) => (
                <button
                  key={industry.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur
                    // Use the callback which handles state + localStorage
                    onSelectIndustry(industry);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="text-sm font-medium text-gray-900">{industry.industry_name} ({industry.business_type === 'private' ? 'Private' : 'Public'})</div>
                  <div className="text-xs text-gray-500">{industry.naics_code} • {industry.industry_parent}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Industry Selection - Two-tier dropdown */}
      <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          Industry Category *
        </label>
        <select
          value={selectedParent || ''}
          onChange={handleParentChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
        >
          <option value="">Select industry category...</option>
          <option value="MANUAL">Manual / Other Industry</option>
          {industryParents.map(parent => (
            <option key={parent} value={parent}>
              {parent}
            </option>
          ))}
        </select>

        {selectedParent === 'MANUAL' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
              Enter Industry Name *
            </label>
            <input
              type="text"
              placeholder="e.g., Custom Manufacturing, Specialty Retail..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              When using manual industry entry, only DCF, Capitalization of Earnings, NAV, and Manual Multiple valuation methods are available.
            </p>
          </>
        )}

        {selectedParent && selectedParent !== 'MANUAL' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
              Specific Industry *
            </label>
            <select
              value={selectedIndustry || ''}
              onChange={handleIndustrySelect}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select specific industry...</option>
              {filteredIndustries.map(industry => (
                <option key={industry.id} value={industry.id.toString()}>
                  {industry.industry_name} ({industry.business_type === 'private' ? 'Private' : 'Public'})
                </option>
              ))}
            </select>
          </>
        )}

        {selectedIndustryDetails && selectedIndustryDetails.multiples && (
          <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm">
            <div className="flex items-center justify-between">
              <p className="font-medium text-blue-900">Industry Multiples ({selectedIndustryDetails.business_type === 'private' ? 'Private' : 'Public'} Companies)</p>
              <div className="flex items-center gap-3 text-blue-700">
                <div>EV/EBITDA: <span className="font-semibold">{selectedIndustryDetails.multiples?.ev_ebitda?.median || selectedIndustryDetails.ev_ebitda || 'N/A'}x</span></div>
                <div>EV/Revenue: <span className="font-semibold">{selectedIndustryDetails.multiples?.ev_sales?.median || selectedIndustryDetails.ev_revenue || 'N/A'}x</span></div>
                <div>EBITDA Margin: <span className="font-semibold">{selectedIndustryDetails.margins?.ebitda_margin?.median || 'N/A'}%</span></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Valuation Method */}
      <div className="mb-6 p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-800">
            Valuation Method
          </label>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onShowMethodDetails && onShowMethodDetails('cca');
            }}
            className="text-blue-600 text-sm hover:underline flex items-center gap-1"
          >
            <HelpCircle size={14} />
            Learn about methods
          </button>
        </div>
        <select
          value={selectedMethod}
          onChange={(e) => setSelectedMethod(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {selectedParent !== 'MANUAL' && <option value="cca">CCA (Comparable Company Analysis)</option>}
          <option value="dcf">DCF (Discounted Cash Flow)</option>
          <option value="capitalization">Capitalization of Earnings</option>
          <option value="nav">NAV (Net Asset Value)</option>
          <option value="manual">Manual Multiple</option>
        </select>

        {/* CCA Multiple Selection */}
        {selectedMethod === 'cca' && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-gray-900 mb-3">Select Primary Multiple for CCA</h4>
            <div>
              <select
                value={ccaMultipleType}
                onChange={(e) => setCcaMultipleType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ev_ebitda">EV/EBITDA (Recommended)</option>
                <option value="ev_sales">EV/Revenue</option>
              </select>
            </div>
            {selectedIndustryDetails && selectedIndustryDetails.multiples && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs font-medium text-gray-700 mb-1">Industry Benchmark for Selected Multiple:</p>
                <p className="text-sm text-blue-700 font-semibold">
                  {selectedIndustryDetails.industry_name}: {
                    ccaMultipleType === 'ev_ebitda'
                      ? `${selectedIndustryDetails.multiples?.ev_ebitda?.median || selectedIndustryDetails.ev_ebitda || 'N/A'}x (EV/EBITDA)`
                      : `${selectedIndustryDetails.multiples?.ev_sales?.median || selectedIndustryDetails.ev_revenue || 'N/A'}x (EV/Revenue)`
                  }
                </p>
              </div>
            )}
          </div>
        )}

        {/* Manual Multiple Fields */}
        {selectedMethod === 'manual' && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-gray-900 mb-3">Manual Multiple Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Multiple Type
                </label>
                <select
                  value={manualMultipleType}
                  onChange={(e) => setManualMultipleType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ev_ebitda">EV/EBITDA</option>
                  <option value="ev_sales">EV/Revenue</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Multiple Value
                </label>
                <input
                  type="number"
                  value={manualMultiple}
                  onChange={(e) => setManualMultiple(e.target.value)}
                  step="0.1"
                  placeholder="e.g., 5.0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              <strong>Tip:</strong> {
                manualMultipleType === 'ev_ebitda'
                  ? 'Common EV/EBITDA multiples range from 4x-8x for small businesses, 8x-12x for mid-market.'
                  : 'Common EV/Revenue multiples range from 0.5x-2x for most industries, 2x-10x+ for SaaS/tech with high margins.'
              }
            </p>
            {selectedIndustryDetails && selectedIndustryDetails.multiples && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs font-medium text-gray-700 mb-1">Industry Benchmark:</p>
                <p className="text-sm text-blue-700 font-semibold">
                  {selectedIndustryDetails.industry_name}: {
                    manualMultipleType === 'ev_ebitda'
                      ? `${selectedIndustryDetails.multiples?.ev_ebitda?.median || selectedIndustryDetails.ev_ebitda || 'N/A'}x`
                      : `${selectedIndustryDetails.multiples?.ev_sales?.median || selectedIndustryDetails.ev_revenue || 'N/A'}x`
                  }
                </p>
              </div>
            )}
          </div>
        )}

        {/* DCF Fields */}
        {selectedMethod === 'dcf' && methodInputs && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-gray-900 mb-3">DCF Configuration</h4>

            {/* Gordon Growth Checkbox - On Top */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-blue-300">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={methodInputs.dcf_perpetuity || false}
                  onChange={(e) => setMethodInputs({ ...methodInputs, dcf_perpetuity: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">Use Gordon Growth Model (Perpetuity)</span>
              </label>
              <p className="text-xs text-gray-600 mt-1 ml-6">When enabled, uses perpetuity formula instead of multi-period projection</p>
            </div>

            {/* All Fields Inline */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Rate (%)
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onShowHelp('dcf_discount_rate');
                    }}
                    className="ml-1 text-blue-600 hover:text-blue-700"
                  >
                    <HelpCircle size={14} className="inline" />
                  </button>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={methodInputs.dcf_discount_rate || 15}
                    onChange={(e) => setMethodInputs({ ...methodInputs, dcf_discount_rate: e.target.value })}
                    step="0.1"
                    placeholder="15"
                    className="w-full pr-8 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">Default: 15%</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Terminal Growth (%)
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onShowHelp('dcf_growth_rate');
                    }}
                    className="ml-1 text-blue-600 hover:text-blue-700"
                  >
                    <HelpCircle size={14} className="inline" />
                  </button>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={methodInputs.dcf_terminal_growth || 3}
                    onChange={(e) => setMethodInputs({ ...methodInputs, dcf_terminal_growth: e.target.value })}
                    step="0.1"
                    placeholder="3"
                    className="w-full pr-8 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">Default: 3%</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Projection Years
                </label>
                <input
                  type="number"
                  value={methodInputs.dcf_years || 5}
                  onChange={(e) => setMethodInputs({ ...methodInputs, dcf_years: e.target.value })}
                  min="1"
                  max="20"
                  placeholder="5"
                  disabled={methodInputs.dcf_perpetuity}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${methodInputs.dcf_perpetuity ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                <p className="text-xs text-gray-600 mt-1">{methodInputs.dcf_perpetuity ? 'N/A (Gordon Growth)' : 'Default: 5 years'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Annual Growth Rate (%)
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onShowHelp('dcf_annual_growth_rate');
                    }}
                    className="ml-1 text-blue-600 hover:text-blue-700"
                  >
                    <HelpCircle size={14} className="inline" />
                  </button>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={methodInputs.dcf_annual_growth_rate || 5}
                    onChange={(e) => setMethodInputs({ ...methodInputs, dcf_annual_growth_rate: e.target.value })}
                    step="0.1"
                    placeholder="5"
                    disabled={methodInputs.dcf_perpetuity}
                    className={`w-full pr-8 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${methodInputs.dcf_perpetuity ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{methodInputs.dcf_perpetuity ? 'N/A (Gordon Growth)' : 'Default: 5%'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Capitalization Fields */}
        {selectedMethod === 'capitalization' && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-gray-900 mb-3">Capitalization Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Earnings Type
                </label>
                <select
                  value={capitalizationEarningsType}
                  onChange={(e) => setCapitalizationEarningsType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ebitda">EBITDA</option>
                  <option value="net_income">Net Income</option>
                  <option value="sde">SDE (Seller's Discretionary Earnings)</option>
                </select>
                <p className="text-xs text-gray-600 mt-1">
                  <span className="font-semibold">Note:</span> EBITDA is pre-tax; Net Income is after-tax.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Capitalization Rate (%)
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onShowHelp('capitalization_rate');
                    }}
                    className="ml-1 text-blue-600 hover:text-blue-700"
                  >
                    <HelpCircle size={14} className="inline" />
                  </button>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={capitalizationRate}
                    onChange={(e) => setCapitalizationRate(e.target.value)}
                    step="0.1"
                    placeholder="20"
                    className="w-full pr-8 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">Leave blank to use default Cap Rate of 20%</p>
              </div>
            </div>

            {/* Business Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-800 mb-3">
                Business Type
                <span className="text-xs text-gray-600 ml-1 font-normal">(Select one to auto-populate cap rate)</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Growth Business */}
                <div
                  onClick={() => {
                    setBusinessType('growth');
                    setCapitalizationRate('15');
                  }}
                  className={`relative p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    businessType === 'growth'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="business_type"
                      value="growth"
                      checked={businessType === 'growth'}
                      onChange={() => {}}
                      className="w-4 h-4 text-green-600 mt-0.5 mr-2"
                    />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Growth</p>
                      <p className="text-xs text-gray-600 mt-0.5">Cap Rate Range: 10-20%</p>
                      <p className="text-xs text-green-700 font-medium mt-0.5">Default: 15%</p>
                    </div>
                  </div>
                </div>

                {/* Stable Business */}
                <div
                  onClick={() => {
                    setBusinessType('stable');
                    setCapitalizationRate('20');
                  }}
                  className={`relative p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    businessType === 'stable'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="business_type"
                      value="stable"
                      checked={businessType === 'stable'}
                      onChange={() => {}}
                      className="w-4 h-4 text-blue-600 mt-0.5 mr-2"
                    />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Stable</p>
                      <p className="text-xs text-gray-600 mt-0.5">Cap Rate Range: 15-25%</p>
                      <p className="text-xs text-blue-700 font-medium mt-0.5">Default: 20%</p>
                    </div>
                  </div>
                </div>

                {/* Declining Business */}
                <div
                  onClick={() => {
                    setBusinessType('declining');
                    setCapitalizationRate('30');
                  }}
                  className={`relative p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    businessType === 'declining'
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="business_type"
                      value="declining"
                      checked={businessType === 'declining'}
                      onChange={() => {}}
                      className="w-4 h-4 text-amber-600 mt-0.5 mr-2"
                    />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Declining</p>
                      <p className="text-xs text-gray-600 mt-0.5">Cap Rate Range: 25-35%</p>
                      <p className="text-xs text-amber-700 font-medium mt-0.5">Default: 30%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Private Company Discount Slider */}
      <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Percent className="text-purple-600" size={20} />
            <label className="text-sm font-semibold text-gray-800">
              Private Company Discount
            </label>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onShowDiscountInfo();
              }}
              className="text-purple-600 hover:text-purple-700"
            >
              <HelpCircle size={16} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-600">
              {privateDiscount === 0 && "Using public company multiples (no discount)"}
              {privateDiscount > 0 && privateDiscount < 25 && "Lower discount = Higher valuation"}
              {privateDiscount === 25 && "Standard private company discount applied"}
              {privateDiscount > 25 && "Higher discount = More conservative valuation"}
            </span>
            <span className="text-2xl font-bold text-purple-600">
              {privateDiscount}%
            </span>
          </div>
        </div>

        <input
          type="range"
          min="0"
          max="50"
          step="5"
          value={privateDiscount}
          onChange={(e) => setPrivateDiscount(parseInt(e.target.value))}
          className="w-full h-3 bg-gradient-to-r from-purple-200 to-pink-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
          style={{
            background: `linear-gradient(to right, #9333ea 0%, #9333ea ${privateDiscount * 2}%, #e9d5ff ${privateDiscount * 2}%, #e9d5ff 100%)`
          }}
        />

        <div className="flex justify-between text-xs text-gray-600 mt-2">
          <span>0% (No discount)</span>
          <span>25% (Standard)</span>
          <span>50% (Maximum)</span>
        </div>
      </div>

      {/* Financial Summary - Shows data from other tabs */}
      <div className="mb-6 p-4 bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Financial Summary (Current Year: {currentYear})</h3>
          <p className="text-xs text-gray-600">Data from Income, Balance Sheet, and Cash Flow tabs</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white p-3 rounded-lg border border-orange-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">Revenue</label>
            <p className="text-lg font-bold text-gray-900">${formatNumberWithCommas(currentYearRevenue)}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-orange-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">EBITDA</label>
            <p className="text-lg font-bold text-gray-900">${formatNumberWithCommas(currentYearEBITDA)}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-orange-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">Net Income</label>
            <p className="text-lg font-bold text-gray-900">${formatNumberWithCommas(currentYearNetIncome)}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-orange-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">SDE</label>
            <p className="text-lg font-bold text-gray-900">${formatNumberWithCommas(currentYearSDE)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-3 rounded-lg border border-orange-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">Free Cash Flow</label>
            <p className="text-lg font-bold text-gray-900">${formatNumberWithCommas(currentYearCashFlow)}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-orange-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">Total Assets</label>
            <p className="text-lg font-bold text-gray-900">${formatNumberWithCommas(currentYearAssets)}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-orange-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">Total Liabilities</label>
            <p className="text-lg font-bold text-gray-900">${formatNumberWithCommas(currentYearLiabilities)}</p>
          </div>
        </div>

        <p className="text-xs text-gray-600 mt-4 italic">
          💡 Tip: Go to the Income, Balance Sheet, and Cash Flow tabs to enter or edit your financial data across multiple years.
        </p>
      </div>
    </div>
  );
};

CalculationSettings.propTypes = {
  selectedMethod: PropTypes.string.isRequired,
  setSelectedMethod: PropTypes.func.isRequired,
  onShowMethodDetails: PropTypes.func,
  industries: PropTypes.array.isRequired,
  selectedParent: PropTypes.string,
  setSelectedParent: PropTypes.func.isRequired,
  selectedIndustry: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedIndustry: PropTypes.func.isRequired,
  industrySearch: PropTypes.string.isRequired,
  searchResults: PropTypes.array.isRequired,
  showSearchDropdown: PropTypes.bool.isRequired,
  onIndustrySearchChange: PropTypes.func.isRequired,
  onSelectIndustry: PropTypes.func.isRequired,
  onDropdownClose: PropTypes.func.isRequired,
  industryLoading: PropTypes.bool,
  ccaMultipleType: PropTypes.string.isRequired,
  setCcaMultipleType: PropTypes.func.isRequired,
  manualMultipleType: PropTypes.string.isRequired,
  setManualMultipleType: PropTypes.func.isRequired,
  manualMultiple: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setManualMultiple: PropTypes.func.isRequired,
  privateDiscount: PropTypes.number.isRequired,
  setPrivateDiscount: PropTypes.func.isRequired,
  onShowDiscountInfo: PropTypes.func.isRequired,
  methodInputs: PropTypes.object,
  setMethodInputs: PropTypes.func.isRequired,
  capitalizationEarningsType: PropTypes.string,
  setCapitalizationEarningsType: PropTypes.func.isRequired,
  businessType: PropTypes.string,
  setBusinessType: PropTypes.func.isRequired,
  capitalizationRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setCapitalizationRate: PropTypes.func.isRequired,
  financialData: PropTypes.object.isRequired,
  currentYear: PropTypes.number.isRequired,
  formatNumberWithCommas: PropTypes.func.isRequired,
  selectedIndustryDetails: PropTypes.object
};

export default CalculationSettings;
